import { useCallback, useEffect, useRef } from 'react';
import { MarketplaceLogo } from 'src/components/common/MarketplaceLogo';
import { ListingPriceInput } from 'src/components/Input/ListingPriceInput';
import { BulkMarketplacePriceFormInput } from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeMarketplacePriceAction/BulkChangeMarketplacePriceAction.types';
import {
  formatOfficeFeePercent,
  getOfficeFeeBucketContentId,
  OfficePriceRow,
} from 'src/components/Listings/InventoryActionDropdown/components/BulkChangeMarketplacePriceAction/officePriceRows.utils';
import { Content } from 'src/contexts/ContentContext';
import { SimpleTable, Stack } from 'src/core/ui';
import { DivWithTitle } from 'src/core/ui/DivWithTitle';
import { ContentId } from 'src/utils/constants/contentId';
import { Event, Marketplace } from 'src/WebApiController';

import * as economicsStyles from './EconomicsSection.css';
import * as styles from './MarketplaceBroadcastSection/MarketplaceDisplay.css';

type OfficeStubHubPriceRowsProps = {
  officePriceRows: OfficePriceRow[];
  event?: Event | null;
  currencyCode: string;
  disabled?: boolean;
  onPriceChanged: () => void;
};

/**
 * Bulk edit, multi-office selection: one StubHub price row per office, each bound to
 * officePriceUpdates.{i} and converted with that office's StubHub seller fee.
 */
export const OfficeStubHubPriceRows = ({
  officePriceRows,
  event,
  currencyCode,
  disabled,
  onPriceChanged,
}: OfficeStubHubPriceRowsProps) => {
  // Proceeds inputs per office row, for Enter-to-next-row navigation
  const proceedsInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusRow = useCallback((index: number) => {
    const input = proceedsInputRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  }, []);

  useEffect(() => {
    // Mirror the single StubHub row: focus the first price input on open
    if (disabled) return;
    const timeout = setTimeout(() => focusRow(0), 100);
    return () => clearTimeout(timeout);
  }, [disabled, focusRow]);

  const onEnter = (index: number) => {
    focusRow((index + 1) % officePriceRows.length);
    onPriceChanged();
  };

  return (
    <>
      {officePriceRows.map(
        ({ officeId, officeName, sellerFee, isDomestic }, i) => {
          const fee = sellerFee ?? 0;
          const bucketContentId = getOfficeFeeBucketContentId(isDomestic);
          return (
            <SimpleTable.Tr
              className={styles.broadcastRow}
              key={`StubHub-office-${officeId}-${isDomestic}`}
            >
              <SimpleTable.Td className={styles.broadcastCell}>
                <Stack direction="row" alignItems="center" gap="s">
                  <MarketplaceLogo
                    marketplace={Marketplace.StubHub}
                    event={event!}
                    isViagogo={false}
                  />
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap="s"
                    className={economicsStyles.officeLabel}
                  >
                    <span>—</span>
                    <DivWithTitle
                      className={economicsStyles.officeName}
                      align="left"
                      title={officeName ?? undefined}
                    >
                      {officeName ?? (
                        <>
                          <Content id={ContentId.Office} /> {officeId}
                        </>
                      )}
                    </DivWithTitle>
                    {bucketContentId ? (
                      <span>
                        {'('}
                        <Content id={bucketContentId} />
                        {')'}
                      </span>
                    ) : (
                      <span>{`(${formatOfficeFeePercent(sellerFee)}%)`}</span>
                    )}
                  </Stack>
                </Stack>
              </SimpleTable.Td>
              <SimpleTable.Td className={styles.broadcastCell}>
                <ListingPriceInput<BulkMarketplacePriceFormInput>
                  ref={(el) => {
                    proceedsInputRefs.current[i] = el;
                  }}
                  isBulkEdit
                  currencyCode={currencyCode}
                  disabled={disabled}
                  field={`officePriceUpdates.${i}.listPrice`}
                  otherField={`officePriceUpdates.${i}.allInPrice`}
                  fieldToOtherRatio={1 - fee}
                  onChange={() => onPriceChanged()}
                  onEnter={() => onEnter(i)}
                />
              </SimpleTable.Td>
              <SimpleTable.Td className={styles.broadcastCell}>
                <ListingPriceInput<BulkMarketplacePriceFormInput>
                  isBulkEdit
                  currencyCode={currencyCode}
                  disabled={disabled}
                  isAllInPrice
                  field={`officePriceUpdates.${i}.allInPrice`}
                  otherField={`officePriceUpdates.${i}.listPrice`}
                  fieldToOtherRatio={1 / (1 - fee)}
                  onChange={() => onPriceChanged()}
                  onEnter={() => onEnter(i)}
                />
              </SimpleTable.Td>
            </SimpleTable.Tr>
          );
        }
      )}
    </>
  );
};
